import { Box, Grid, Link, styled } from '@mui/material';
import SitePlaceholderImage from 'assets/img/site-placeholder.png';
import FarmPlots from 'FeatureModules/Site/Components/FarmPlots';
import PaginatedMap from 'FeatureModules/Site/Components/PaginatedMap';
import PlotItem from 'FeatureModules/Site/Components/PlotItem';
import PolygonDataCard from 'FeatureModules/Site/Components/PolygonDataCard';
import Loader from 'Components/Forms/Loader';
import useDialog from 'hooks/useDialog';
import usePageTitle from 'hooks/usePageTitle';
import ActivityItem from 'FeatureModules/Activity/Components/ActivitiesTable/ActivityItem';
import PartnerDocumentList from 'FeatureModules/Partner/Components/PartnerDocuments/PartnerDocumentList';
import OverviewHeader from 'Components/OverviewHeader/OverviewHeader';
import SiteTable from 'FeatureModules/Site/Components/SiteTable/SiteTable';
import { PREVIEW_LATEST_ITEMS } from 'utils/pagination.utils';
import { ThemeButton } from 'DesignSystem';
import CountryRiskLevel from 'DesignSystem/DataDisplay/CountryRiskLevel/CountryRiskLevel';
import ItemSummary from 'DesignSystem/DataDisplay/ItemSummary/ItemSummary';
import LogSummary from 'DesignSystem/DataDisplay/LogSummary/LogSummary';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import useSite from 'FeatureModules/Site/hooks/useSite';
import useSiteActions from 'FeatureModules/Site/hooks/useSiteActions';
import React, { FC, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import { ESiteType, IFarmSite } from 'FeatureModules/Site/types/site.types';
import { getCountryRiskLevelFromCode } from 'utils/country.utils';
import {
  getSiteExtraInfo,
  getSiteSecondTitle,
  getSiteTitle,
  SiteTypeLabels,
} from 'FeatureModules/Site/utils/site.utils';
import { SeeAllLink } from '../../Partner/Pages/PartnerOverview';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageSubTitle from 'Components/PageSubTitle';
import PageTitle from 'Components/PageTitle';

const LinkContainer = styled(Box)(({ theme }) => ({
  a: {
    textDecoration: 'none',
  },
}));

export const OverviewHeaderContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  flex: '1',
  border: `${theme.custom.colors.lightBorderColor} 1px solid`,
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: 6,
  overflow: 'hidden',
}));

const SiteOverview: FC = () => {
  usePageTitle('Site overview');
  const {
    site,
    loading,
    loadingPlots,
    plots,
    hasClusterMorePlots,
    error,
    internalSiteDocuments,
    sharedSiteDocuments,
    totalSharedSiteDocumentsCount,
    siteActivities,
    totalSiteActivitiesCount,
    totalInternalSiteDocumentsCount,
    totalPlotsCount,
    handleLoadMorePlots,
    handleEditSite,
    handleCreateSiteDocument,
    handleCreateSiteDocumentRequest,
    handleAddActivity,
    handleEditActivity,
  } = useSite();
  const { handleManageSite, handleCreateSite, handleEditFarmPlots } = useSiteActions();
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const { company } = useCompanyPlanContext();

  const isCluster = site?.siteType === ESiteType.CLUSTER;
  const latestLog = useMemo(() => {
    if (!site?.logs || site.logs.length === 0) return undefined;
    const rawLatestLog = site.logs.reduce((latest, current) =>
      new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
    );
    return {
      date: rawLatestLog.timestamp,
      logo: rawLatestLog.company?.logo?.url,
      author: !!rawLatestLog.user
        ? `${rawLatestLog.user.firstName} ${rawLatestLog.user.lastName}`
        : rawLatestLog.userEmail,
      source: rawLatestLog.source,
    };
  }, [site]);

  const handleCreateSiteForCluster = () => {
    if (!isCluster) return;
    handleCreateSite({
      siteClusterId: site.id,
      defaultSiteType: site?.sites?.edges[0]?.node.siteType,
      defaultRawMaterialId: (site?.sites?.edges[0]?.node as IFarmSite)?.rawMaterial?.id,
      defaultCountry: site?.locationName,
    });
  };

  const handleEditPlot = (plot: IPlot) => {
    openDialog({
      type: 'ADD_EDIT_FARM_PLOT',
      props: {
        plot,
      },
    });
  };

  const handleAddPlot = () => {
    if (site?.siteType !== ESiteType.FARM) {
      console.error('Creating a plot is only available for FARM sites');
      return;
    }

    openDialog({
      type: 'ADD_EDIT_FARM_PLOT',
      props: {
        farmId: site.id,
      },
    });
  };

  if (loading) {
    return <Loader />;
  }
  if (error || !site) {
    return <ErrorState action={undefined} />;
  }

  const countryRiskLevel = getCountryRiskLevelFromCode(site.countryCode);

  return (
    <PageContainer>
      <PageTitle
        enableAutoGoBackHistoryButton
        title={isCluster ? 'Cluster overview' : 'Site overview'}
        goBackLabel="All sites"
        goBackUrl="/sites"
        titleExtension={site.title}
      />
      <Box mb={3} />

      <Grid container spacing={3}>
        <Grid item xs={12} xl={9}>
          <OverviewHeader image={site.image} placeholderImageUrl={SitePlaceholderImage}>
            <Box
              display="flex"
              alignItems="flex-start"
              flexDirection="column"
              height="100%"
              justifyContent="space-evenly"
            >
              <ItemSummary
                size="large"
                title={getSiteTitle({
                  site,
                  farmDisplayOption: company.farmDisplayOption,
                  siteDisplayOption: company.siteDisplayOption,
                })}
                externalId={getSiteSecondTitle({
                  site,
                  farmDisplayOption: company.farmDisplayOption,
                  siteDisplayOption: company.siteDisplayOption,
                })}
                extraInfo={getSiteExtraInfo({
                  site,
                  farmDisplayOption: company.farmDisplayOption,
                  siteDisplayOption: company.siteDisplayOption,
                })}
                TitleEndAdornment={
                  countryRiskLevel && <CountryRiskLevel riskLevel={countryRiskLevel} />
                }
                details={[
                  {
                    icon: 'tag-filled',
                    title:
                      site.siteType === ESiteType.CLUSTER && site.sitesType
                        ? `${SiteTypeLabels[site.sitesType]} cluster`
                        : SiteTypeLabels[site.siteType],
                  },
                  site.siteType === ESiteType.CLUSTER &&
                    !!site.sitesType && {
                      icon: 'farm',
                      title: `${site.sitesCount} ${SiteTypeLabels[site.sitesType].toLowerCase()}${
                        site.sitesCount <= 1 ? '' : 's'
                      }`,
                    },
                  site.siteType === ESiteType.CLUSTER &&
                    !!site.sitesRawMaterial && {
                      icon: 'raw-material',
                      title: site.sitesRawMaterial.title,
                    },
                  site.siteType === ESiteType.FARM &&
                    !!site.rawMaterial && {
                      icon: 'raw-material',
                      title: site.rawMaterial.title,
                    },
                  {
                    icon: 'location',
                    title: site.locationName,
                  },
                  !!site.ownedBy && {
                    icon: 'partner',
                    title: site.ownedBy?.name,
                    navigateTo: `/partners/${site.ownedBy.id}`,
                  },
                ]}
              />
              <FlexBox gap={2}>
                <ThemeButton
                  startIcon={<Icon name="edit" color="WHITE" />}
                  size="large"
                  color="PRIMARY"
                  loading={false}
                  onClick={() => handleEditSite()}
                >
                  {isCluster ? 'Edit cluster' : 'Edit site'}
                </ThemeButton>

                {site.siteType === ESiteType.FARM && plots.length > 0 && (
                  <ThemeButton
                    startIcon={<Icon name="polygon-mapping" color="WHITE" />}
                    size="large"
                    color="PRIMARY"
                    loading={false}
                    onClick={() => handleEditFarmPlots(site)}
                  >
                    Edit cultivation areas
                  </ThemeButton>
                )}

                <ThemeButton
                  startIcon={<Icon name="site-cluster-manage" />}
                  size="large"
                  color="BLUE_ICE"
                  onClick={() => handleManageSite(site)}
                >
                  {isCluster
                    ? 'Manage cluster sites'
                    : site.siteClusterId
                    ? 'Change site cluster'
                    : 'Add site to cluster'}
                </ThemeButton>

                {site.siteType === ESiteType.FARM && plots.length > 0 && (
                  <ThemeButton
                    size="large"
                    color="BLUE_ICE"
                    onClick={() => handleEditFarmPlots(site, 'json')}
                  >
                    View plot coordinates
                  </ThemeButton>
                )}
              </FlexBox>
            </Box>
          </OverviewHeader>
        </Grid>

        {/* History of site logs */}
        <Grid item xs={12} lg={6} xl={3}>
          <PageSubTitle title="History" mb={2} display={{ xl: 'none' }} />
          <OverviewHeaderContainer height={{ xl: '100%' }}>
            {latestLog && (
              <LogSummary latestLog={latestLog} createdTimestamp={site.createdTimestamp} />
            )}
          </OverviewHeaderContainer>
        </Grid>

        {isCluster && (
          <>
            {!!site.sitesCount && (
              <Grid item xs={6}>
                <Box flex={1}>
                  <PageSubTitle
                    title="Cluster Sites"
                    visibleItems={site.sites.edges.length}
                    totalItems={site.sitesCount}
                    mb={2}
                  >
                    <LinkContainer display="flex" alignItems="center" gap={2}>
                      <Link
                        component={RouterLink}
                        to="sites"
                        color="secondary"
                        className="small"
                        data-testid="link-see-all-sites"
                      >
                        See all
                      </Link>
                      <ThemeButton
                        startIcon={<Icon name="plus" size="small" />}
                        size="small"
                        color="WHITE"
                        onClick={handleCreateSiteForCluster}
                      >
                        Add new
                      </ThemeButton>
                    </LinkContainer>
                  </PageSubTitle>

                  <SiteTable previewMode sites={site.sites.edges.map(edge => edge.node)} />
                </Box>
              </Grid>
            )}

            <Grid item xs={6}>
              <PaginatedMap
                plots={plots}
                minHeight={350}
                hasNextPage={hasClusterMorePlots}
                loading={loadingPlots}
                itemCount={totalPlotsCount}
                onLoadMorePlots={handleLoadMorePlots}
              />
            </Grid>
          </>
        )}

        {site.siteType === ESiteType.FARM && (
          <Grid item xs={6}>
            <Box flex={1} minHeight={400}>
              <PageSubTitle
                title="Cultivation areas"
                visibleItems={plots.length}
                totalItems={totalPlotsCount}
                spaceBetween={false}
                mb={2}
              />
              {plots.length === 0 && !loadingPlots ? (
                <PolygonDataCard site={site} onAddPlotClick={handleAddPlot} />
              ) : (
                // 38px from the SubTitle header
                <Box height="calc(100% - 38px)" width="100%">
                  <FarmPlots minHeight={350} plots={plots} />
                </Box>
              )}
            </Box>
          </Grid>
        )}

        {site.siteType === ESiteType.FARM && plots.length > 0 && (
          <Grid item xs={6}>
            <Box flex={1}>
              <PageSubTitle
                title="Site Plots"
                visibleItems={plots.length}
                totalItems={totalPlotsCount}
                mb={2}
              >
                <LinkContainer display="flex" alignItems="center" gap={2}>
                  <Link
                    component={RouterLink}
                    to="plots"
                    color="secondary"
                    className="small"
                    data-testid="link-see-all-plots"
                  >
                    See all plots
                  </Link>
                  <ThemeButton
                    startIcon={<Icon name="plus" size="small" />}
                    size="small"
                    color="WHITE"
                    onClick={handleAddPlot}
                  >
                    Add new
                  </ThemeButton>
                </LinkContainer>
              </PageSubTitle>
              <Box display="flex" flexDirection="column" gap={1}>
                {plots.slice(0, 4).map(plot => (
                  <PlotItem
                    key={plot.id}
                    hideSeverity
                    plot={plot}
                    onEdit={() => handleEditPlot(plot)}
                    onClick={() => navigate(`plots/${plot.id}`)}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        )}

        {/* Latest site activities */}
        {!!siteActivities.length && (
          <Grid item xs={12} lg={6}>
            <Box position="relative">
              <PageSubTitle
                title="Activities"
                visibleItems={siteActivities.length}
                totalItems={totalSiteActivitiesCount}
                mb={1}
              >
                <FlexBox gap={1}>
                  {!!totalSiteActivitiesCount &&
                    totalSiteActivitiesCount > PREVIEW_LATEST_ITEMS && (
                      <SeeAllLink to={`activities`}>See all activities</SeeAllLink>
                    )}
                  <ThemeButton
                    onClick={handleAddActivity}
                    color="WHITE"
                    size="small"
                    startIcon={<Icon name="plus" />}
                  >
                    Add new
                  </ThemeButton>
                </FlexBox>
              </PageSubTitle>
              {siteActivities.map(activity => (
                <ActivityItem
                  key={activity.id}
                  activity={activity}
                  onEdit={() => handleEditActivity(activity)}
                  onClick={() => navigate(`/activities/${activity.id}`)}
                />
              ))}
            </Box>
          </Grid>
        )}

        {totalInternalSiteDocumentsCount > 0 && internalSiteDocuments && (
          <Grid item xs={6}>
            <PartnerDocumentList
              title="Internal documents"
              actionButtonText="Add new"
              totalItems={totalInternalSiteDocumentsCount}
              documents={internalSiteDocuments}
              onCreateClick={handleCreateSiteDocument}
              onAllDocumentsClick={() => navigate('documents/internal')}
            />
          </Grid>
        )}
        {totalSharedSiteDocumentsCount > 0 && sharedSiteDocuments && (
          <Grid item xs={6}>
            <PartnerDocumentList
              title="Shared documents"
              actionButtonText="Request new"
              totalItems={totalSharedSiteDocumentsCount}
              documents={sharedSiteDocuments}
              onCreateClick={handleCreateSiteDocumentRequest}
              onAllDocumentsClick={() => navigate('documents/shared')}
            />
          </Grid>
        )}
      </Grid>
    </PageContainer>
  );
};

export default SiteOverview;
